// Map backend response shapes into the shapes the existing frontend components
// already consume, so the bulk of the UI needs no changes.
import { assetUrl } from "./client";

// Backend Course: { id, name, description, status, owner:{id,full_name},
//   editors:[{id,full_name}], case_count, created_at, updated_at }
export const adaptCourse = (c) => ({
  id: c.id,
  name: c.name,
  description: c.description,
  status: c.status,
  owner: c.owner?.full_name ?? "",
  ownerId: c.owner?.id ?? null,
  editors: (c.editors || []).map((e) => e.full_name),
  editorIds: (c.editors || []).map((e) => e.id),
  caseCount: c.case_count ?? 0,
  createdAt: c.created_at,
  updatedAt: c.updated_at,
});

// Backend Case: { id, key, pathology, region, clinical_summary, course_id,
//   course_name, owner:{id,full_name}, date, images:[{...,url}] }
export const adaptCase = (c) => ({
  key: c.key ?? c.id,
  id: c.id,
  pathology: c.pathology,
  organ: c.organ ?? "",
  region: c.region,
  clinicalSummary: c.clinical_summary ?? "",
  course: c.course_id ?? null,
  courseName: c.course_name ?? null,
  owner: c.owner?.full_name ?? "",
  ownerId: c.owner?.id ?? null,
  date: c.date,
  images: (c.images || []).map(adaptImage),
});

export const adaptImage = (img) => ({
  id: img.id, // asset id
  modality: img.modality,
  name: img.name || "",
  assetType: img.asset_type,
  url: assetUrl(img.url),
  thumbnailUrl: img.thumbnail_url ? assetUrl(img.thumbnail_url) : null,
});

// Backend Annotation: { id, case_id, asset_id, modality_key, name, description,
//   tool_name, geometry (opaque JSON), author_id, author_name, author_role, created_at }
// Flatten geometry back to top level so viewers see bounds/points directly.
export const adaptAnnotation = (a) => ({
  id: a.id,
  name: a.name,
  description: a.description,
  toolName: a.tool_name,
  authorRole: a.author_role,
  authorName: a.author_name,
  createdAt: a.created_at,
  modalityKey: a.modality_key,
  assetId: a.asset_id ?? null,
  ...(a.geometry || {}),
});

// Build the POST payload for an annotation from the hook's in-memory object.
// Radio annotations carry `points`; OSD/SVG (macro, histo) carry `geometry` (+bounds).
export const toAnnotationPayload = (modalityKey, anno) => {
  const { bounds, geometry, points, sliceIndex, frameOfReferenceUID, color } = anno;
  const geo = points
    ? { points, sliceIndex, frameOfReferenceUID }
    : { bounds, geometry };
  // Color rides inside the opaque geometry JSON (no schema change needed);
  // adaptAnnotation spreads geometry back to the top level on read.
  if (color) geo.color = color;
  return {
    modality_key: modalityKey,
    asset_id: anno.assetId ?? null,
    name: anno.name || "",
    description: anno.description || "",
    tool_name: anno.toolName,
    geometry: geo,
  };
};

// Group an already-adapted annotations array (from listAnnotations) into the
// keyed map the viewer expects: { "macro": [...], "radio:series1": [...], ... }.
export const groupAnnotations = (list) => {
  const out = {};
  (list || []).forEach((a) => {
    const key = a.modalityKey;
    if (!key) return;
    if (!out[key]) out[key] = [];
    out[key].push(a);
  });
  return out;
};
